import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Chip, Box, Button, Typography, Card, CardContent, CardHeader,
  FormControl, Select, MenuItem, CircularProgress, InputLabel
} from '@mui/material';
import BrowseIcon from '@mui/icons-material/Folder';
import TemplateIcon from '@mui/icons-material/Description';
import CollectionIcon from '@mui/icons-material/FolderSpecial';
import { useTranslation } from 'react-i18next';
import { useWebSocket } from '../WebSocketContext';
import TagsDropdown from '../TagsDropdown';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import TagEditor from '../Widgets/TagEditor';
import { fetchCollections } from '../../Utils/collectionsApi';

const ActionsCreateProject = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const { t } = useTranslation();
  const { sendMessage, subscribeToMessages } = useWebSocket();
  
  // States for different fields
  const [title, setTitle] = useState('');
  const [folder, setFolder] = useState('');
  const titleRef = useRef(null);
  const folderRef = useRef(null);
  
  // Template and collection states
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedCollectionIds, setSelectedCollectionIds] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [collections, setCollections] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  
  // Handle template selection
  const handleTemplateChange = (templateId) => {
    setSelectedTemplateId(templateId);
  };
  
  // UI states
  const [browsingDisabled, setBrowsingDisabled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuType, setMenuType] = useState('');

  const { handleBrowseDirectory, FolderBrowserComponent } = useFolderPicker({
    onFolderSelected: (folderPath) => {
      if (folderPath) {
        let folderPathClean = folderPath.replace(/\/+$/, "");
        setFolder(folderPathClean);
      }
      setBrowsingDisabled(false);
    },
    onFolderBrowserClose: () => {
      setBrowsingDisabled(false);
    }
  });

  // Load templates and collections through HTTP-backed commands.
  useEffect(() => {
    const messageHandler = (parsedMessage) => {
      console.log('ActionsCreateProject received message:', parsedMessage);
      
      switch (parsedMessage.command) {
        case 'getAllTemplates':
          setTemplatesLoading(false);
          if (parsedMessage.status === 'success' || (parsedMessage.data && parsedMessage.data.status === 'success')) {
            // Handle both direct response and nested data response
            const templates = parsedMessage.templates || (parsedMessage.data && parsedMessage.data.templates) || [];
            console.log('Templates received:', templates);
            setTemplates(templates);
          } else {
            const errorMessage = parsedMessage.message || 
                                (parsedMessage.data && parsedMessage.data.message) || 
                                'Failed to load templates';
            console.error('Failed to load templates:', errorMessage);
            setTemplates([]);
          }
          break;
          
        default:
          // Ignore other commands
          break;
      }
    };

    subscribeToMessages(messageHandler);
    
    // Load data when component mounts
    setTemplatesLoading(true);
    setCollectionsLoading(true);
    sendMessage({ command: 'getAllTemplates' });
    fetchCollections()
      .then((payload) => {
        const collections = Array.isArray(payload.collections) ? payload.collections : [];
        console.log('Collections received:', collections);
        setCollections(collections);
      })
      .catch((error) => {
        console.error('Failed to load collections:', error);
        setCollections([]);
      })
      .finally(() => {
        setCollectionsLoading(false);
      });

    return () => {
      // No cleanup needed for subscribeToMessages as it handles its own cleanup
    };
  }, [sendMessage, subscribeToMessages]);

  const actionSettings = useActionSettings({
    settingId,
    caseid,
    actionType: 'createproject',
    onSaveStatusChange,
    customComparison: (initial) => {
      // Handle different possible data structures for initial data
      const initialData = initial.customData || initial;
      
      return title === (initialData.projectTitle || '') &&
             folder === (initialData.folderPath || '') &&
             selectedTemplateId === (initialData.templateId || '') &&
             JSON.stringify(selectedCollectionIds.sort()) === JSON.stringify((initialData.collectionIds || []).sort());
    },
    customSaveData: () => {
      const saveData = {
        projectTitle: title,
        folderPath: folder,
        templateId: selectedTemplateId || null,
        collectionIds: selectedCollectionIds,
        projectGoal: ""  // Add explicit empty goal field for backend compatibility
      };
      
      console.log('ActionsCreateProject - Saving data:', saveData);
      
      return saveData;
    },
    loadDataFunction: (data) => {
      // Handle different possible data structures
      // The data might be in data.customData or directly in data
      let sourceData = data.customData || data;
      
      if (sourceData) {
        const { projectTitle, folderPath, templateId, collectionIds } = sourceData;
        
        if (projectTitle !== undefined) {
          setTitle(projectTitle || '');
        }
        
        if (folderPath !== undefined) {
          setFolder(folderPath || '');
        }
        
        if (templateId !== undefined) {
          handleTemplateChange(templateId || '');
        }
        
        if (collectionIds !== undefined) {
          setSelectedCollectionIds(collectionIds || []);
        }
      }
    }
  });

  // Update save button state when editor states or selections change
  useEffect(() => {
    if (actionSettings.updateSaveDisabledState) {
      actionSettings.updateSaveDisabledState();
    }
  }, [title, folder, selectedTemplateId, selectedCollectionIds, actionSettings]);

  const handleMenuOpen = (event, type) => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
  };

  const handleMainTagMenuClose = () => {
    setAnchorEl(null);
  };

  const handleTagSelect = (value) => {
    if (menuType === 'title') {
      titleRef.current?.insertTag(value);
    } else if (menuType === 'folder') {
      folderRef.current?.insertTag(value);
    }
    setAnchorEl(null);
  };

  const handleTestSave = () => {
    const testData = {
      projectTitle: title,
      folderPath: folder,
      templateId: selectedTemplateId || null,
      collectionIds: selectedCollectionIds,
      projectGoal: ""
    };
    
    console.log('TEST SAVE - Current form data:', testData);
    alert(`Title: "${title}"\nFolder: "${folder}"\nTemplate: ${selectedTemplateId}\nCollections: ${selectedCollectionIds.length}`);
  };

  const quickTags = [
    { label: 'Filename', value: 'filename' },
    { label: 'Year', value: 'year' },
    { label: 'Month', value: 'month' },
    { label: 'Day', value: 'day' },
    { label: 'Summary', value: 'summary' },
  ];

  const QuickTagsSection = ({ onInsertTag, tagMenuType }) => (
    <Box sx={{
      p: 2,
      display: 'flex',
      flexWrap: 'wrap',
      gap: 1,
      backgroundColor: '#f5fff5',
      borderTop: '1px solid #eaeaf0',
    }}>
      <Typography sx={{
        fontWeight: 600,
        color: '#2e7d2e',
        fontSize: '0.85rem',
        mr: 1,
        display: 'flex',
        alignItems: 'center',
      }}>
        Quick Tags:
      </Typography>
      {quickTags.map(tag => (
        <Chip
          key={tag.value}
          label={tag.label}
          onClick={() => onInsertTag(tag.value)}
          size="small"
          sx={{
            backgroundColor: '#e6ffe6',
            color: '#2e7d2e',
            borderColor: '#2e7d2e30',
            border: '1px solid',
            '&:hover': {
              backgroundColor: '#d4eedd',
              boxShadow: '0 2px 6px rgba(46, 125, 46, 0.15)',
            },
            fontWeight: 600,
            transition: 'all 0.2s ease',
          }}
        />
      ))}
    </Box>
  );

  return (
    <ActionSettingsLayout
      title="Create Project"
      isEnabled={actionSettings.isEnabled}
      onEnabledChange={actionSettings.setIsEnabled}
      isSaveDisabled={actionSettings.isSaveDisabled}
      onSave={actionSettings.handleSave}
      onForceRun={actionSettings.handleForceRun}
      forceRunLoading={actionSettings.forceRunLoading}
      conditions={actionSettings.conditions}
      onConditionsChange={actionSettings.handleConditionsChange}
      popUpOpen={actionSettings.popUpOpen}
      forceRunResult={actionSettings.forceRunResult}
      onCloseStatusWindow={actionSettings.handleCloseStatusWindow}
      forceRunWarningOpen={actionSettings.forceRunWarningOpen}
      forceRunWarningCount={actionSettings.forceRunWarningCount}
      onForceRunConfirm={actionSettings.handleForceRunConfirm}
      onForceRunCancel={actionSettings.handleForceRunCancel}
      tags={tags}
      primaryColor="#2e7d2e"
    >
      {/* Project Title */}
      <Card sx={{
        mb: 2,
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        border: '1px solid #2e7d2e10',
        overflow: 'hidden',
      }}>
        <CardHeader
          title={
            <Typography sx={{
              fontWeight: 700,
              color: '#444',
              fontSize: { xs: '1rem', sm: '1.1rem' },
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}>
              <Box component="span" sx={{
                bgcolor: '#2e7d2e',
                color: 'white',
                borderRadius: '6px',
                px: 1.5,
                py: 0.7,
                mr: { xs: 1, sm: 2 },
                fontSize: '0.85rem',
                letterSpacing: '0.5px',
                fontWeight: 600,
                boxShadow: '0 3px 6px rgba(46, 125, 46, 0.2)',
              }}>
                TITLE
              </Box>
              Project title *
            </Typography>
          }
          sx={{
            borderBottom: '1px solid #f0f0f8',
            backgroundColor: '#f8fff8',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            p: { xs: 1.5, sm: 2 },
          }}
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{
            mb: 2,
            border: '1px solid #c0e0c0',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(46, 125, 46, 0.05)',
            backgroundColor: '#fff',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: '#2e7d2e',
              boxShadow: '0 8px 20px rgba(46, 125, 46, 0.1)',
            },
          }}>
            <Box sx={{
              padding: '12px 16px',
              minHeight: '28px',
              cursor: 'text',
            }}>
              <TagEditor
                    caseid={caseid}
                ref={titleRef}
                value={title}
                onChange={setTitle}
                placeholder="Project title (e.g. Legal Review - {filename})"
                singleLine
                tags={tags}
                onTagClick={(e) => handleMenuOpen(e, 'title')}
              />
            </Box>
            <QuickTagsSection 
              onInsertTag={(value) => {
                if (titleRef.current) {
                  titleRef.current.focus();
                  setTimeout(() => {
                    titleRef.current.insertTag(value);
                  }, 10);
                }
              }}
              tagMenuType="title"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Folder Path */}
      <Card sx={{
        mb: 2,
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        border: '1px solid #2e7d2e10',
        overflow: 'hidden',
      }}>
        <CardHeader
          title={
            <Typography sx={{
              fontWeight: 700,
              color: '#444',
              fontSize: { xs: '1rem', sm: '1.1rem' },
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}>
              <Box component="span" sx={{
                bgcolor: '#2e7d2e',
                color: 'white',
                borderRadius: '6px',
                px: 1.5,
                py: 0.7,
                mr: { xs: 1, sm: 2 },
                fontSize: '0.85rem',
                letterSpacing: '0.5px',
                fontWeight: 600,
                boxShadow: '0 3px 6px rgba(46, 125, 46, 0.2)',
              }}>
                FOLDER
              </Box>
              Source data folder *
            </Typography>
          }
          sx={{
            borderBottom: '1px solid #f0f0f8',
            backgroundColor: '#f8fff8',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            p: { xs: 1.5, sm: 2 },
          }}
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{
            mb: 2,
            border: '1px solid #c0e0c0',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(46, 125, 46, 0.05)',
            backgroundColor: '#fff',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: '#2e7d2e',
              boxShadow: '0 8px 20px rgba(46, 125, 46, 0.1)',
            },
          }}>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'stretch',
            }}>
              <Box sx={{
                flex: 1,
                padding: '12px 16px',
                minHeight: '28px',
                borderBottom: { xs: '1px solid #eaeaf0', sm: 'none' },
              }}>
                <TagEditor
                    caseid={caseid}
                  ref={folderRef}
                  value={folder}
                  onChange={setFolder}
                  placeholder="Source folder path (e.g. C:\\Projects\\{year}\\{month})"
                  singleLine
                  tags={tags}
                  onTagClick={(e) => handleMenuOpen(e, 'folder')}
                />
              </Box>
              <Button
                variant="contained"
                onClick={handleBrowseDirectory}
                disabled={browsingDisabled}
                sx={{
                  height: { xs: '40px', sm: 'auto' },
                  width: { xs: '100%', sm: 'auto' },
                  borderRadius: { xs: '0', sm: '0 12px 0 0' },
                  backgroundColor: '#2e7d2e',
                  color: 'white',
                  m: 0,
                  px: 2.5,
                  '&:hover': {
                    backgroundColor: '#1e5d1e',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#c0c0d0',
                    color: 'white',
                  },
                  borderLeft: { sm: '1px solid #2e7d2e60' },
                  transition: 'all 0.2s ease',
                }}
              >
                <BrowseIcon />
              </Button>
            </Box>
            <QuickTagsSection 
              onInsertTag={(value) => {
                if (folderRef.current) {
                  folderRef.current.focus();
                  setTimeout(() => {
                    folderRef.current.insertTag(value);
                  }, 10);
                }
              }}
              tagMenuType="folder"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Template Selection */}
      <Card sx={{
        mb: 2,
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        border: '1px solid #2e7d2e10',
        overflow: 'hidden',
      }}>
        <CardHeader
          title={
            <Typography sx={{
              fontWeight: 700,
              color: '#444',
              fontSize: { xs: '1rem', sm: '1.1rem' },
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}>
              <Box component="span" sx={{
                bgcolor: '#2e7d2e',
                color: 'white',
                borderRadius: '6px',
                px: 1.5,
                py: 0.7,
                mr: { xs: 1, sm: 2 },
                fontSize: '0.85rem',
                letterSpacing: '0.5px',
                fontWeight: 600,
                boxShadow: '0 3px 6px rgba(46, 125, 46, 0.2)',
              }}>
                TEMPLATE
              </Box>
              Template Selection
            </Typography>
          }
          subheader={
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              Choose a template to pre-fill project structure and settings
            </Typography>
          }
          sx={{
            borderBottom: '1px solid #f0f0f8',
            backgroundColor: '#f8fff8',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            p: { xs: 1.5, sm: 2 },
          }}
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          {templatesLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
              <CircularProgress size={20} sx={{ color: '#2e7d2e' }} />
              <Typography variant="body2" color="text.secondary">
                Loading templates...
              </Typography>
            </Box>
          ) : templates.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 3, 
              backgroundColor: '#f9f9f9', 
              borderRadius: '8px',
              border: '1px dashed #ddd'
            }}>
              <TemplateIcon sx={{ color: '#ccc', fontSize: '3rem', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No templates available. Create some templates first to enable template selection.
              </Typography>
            </Box>
          ) : (
            <FormControl 
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fff8 100%)',
                  boxShadow: '0 2px 8px rgba(46, 125, 46, 0.12)',
                  border: '1px solid rgba(46, 125, 46, 0.2)',
                  '&:hover': {
                    borderColor: '#2e7d2e',
                    boxShadow: '0 4px 12px rgba(46, 125, 46, 0.18)',
                  },
                  '&.Mui-focused': {
                    borderColor: '#2e7d2e',
                    boxShadow: '0 0 0 3px rgba(46, 125, 46, 0.1)',
                  },
                  '& fieldset': {
                    border: 'none',
                  }
                }
              }}
            >
              <InputLabel sx={{ fontWeight: 600, color: 'rgba(46, 125, 46, 0.7)' }}>
                Choose a template...
              </InputLabel>
              <Select
                value={selectedTemplateId}
                onChange={(e) => handleTemplateChange(e.target.value)}
                renderValue={(selectedId) => {
                  if (!selectedId) return '';
                  const template = templates.find(t => t.id === selectedId);
                  if (!template) return '';
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '8px',
                          background: 'linear-gradient(135deg, #2e7d2e 0%, #4e9d4e 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 6px rgba(46, 125, 46, 0.3)'
                        }}
                      >
                        <TemplateIcon sx={{ 
                          color: 'white', 
                          fontSize: '1rem'
                        }} />
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {template.template_title || template.name || `Template ${template.id}`}
                        </Typography>
                        {template.version && (
                          <Typography variant="caption" sx={{ 
                            color: 'text.secondary', 
                            fontWeight: 500
                          }}>
                            v{template.version}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                }}
              >
                <MenuItem value="" sx={{ py: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #9e9e9e 0%, #757575 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 6px rgba(158, 158, 158, 0.3)'
                      }}
                    >
                      <Typography sx={{ 
                        color: 'white', 
                        fontSize: '0.9rem', 
                        fontWeight: 'bold'
                      }}>
                        ∅
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        No template (create empty project)
                      </Typography>
                      <Typography variant="caption" sx={{ 
                        color: 'text.secondary', 
                        fontWeight: 400
                      }}>
                        Start with a blank project structure
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
                {templates.map((template) => (
                  <MenuItem key={template.id} value={template.id} sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '10px',
                          background: 'linear-gradient(135deg, #2e7d2e 0%, #4e9d4e 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 6px rgba(46, 125, 46, 0.3)'
                        }}
                      >
                        <TemplateIcon sx={{ 
                          color: 'white', 
                          fontSize: '1rem'
                        }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {template.template_title || template.name || `Template ${template.id}`}
                        </Typography>
                        {(template.template_description || template.description) && (
                          <Typography variant="caption" sx={{ 
                            color: 'text.secondary', 
                            fontWeight: 400,
                            display: 'block',
                            maxWidth: '300px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {template.template_description || template.description}
                          </Typography>
                        )}
                        {template.version && (
                          <Chip 
                            label={`v${template.version}`} 
                            size="small" 
                            sx={{ 
                              mt: 0.5,
                              height: '18px',
                              fontSize: '0.7rem',
                              backgroundColor: 'rgba(46, 125, 46, 0.1)',
                              color: '#2e7d2e',
                              border: '1px solid rgba(46, 125, 46, 0.3)'
                            }} 
                          />
                        )}
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </CardContent>
      </Card>

      {/* Collection Selection */}
      <Card sx={{
        mb: 2,
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        border: '1px solid #2e7d2e10',
        overflow: 'hidden',
      }}>
        <CardHeader
          title={
            <Typography sx={{
              fontWeight: 700,
              color: '#444',
              fontSize: { xs: '1rem', sm: '1.1rem' },
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}>
              <Box component="span" sx={{
                bgcolor: '#2e7d2e',
                color: 'white',
                borderRadius: '6px',
                px: 1.5,
                py: 0.7,
                mr: { xs: 1, sm: 2 },
                fontSize: '0.85rem',
                letterSpacing: '0.5px',
                fontWeight: 600,
                boxShadow: '0 3px 6px rgba(46, 125, 46, 0.2)',
              }}>
                COLLECTIONS
              </Box>
              Collection Assignment
            </Typography>
          }
          subheader={
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              Add this project to one or more collections for better organization
            </Typography>
          }
          sx={{
            borderBottom: '1px solid #f0f0f8',
            backgroundColor: '#f8fff8',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            p: { xs: 1.5, sm: 2 },
          }}
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          {collectionsLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
              <CircularProgress size={20} sx={{ color: '#2e7d2e' }} />
              <Typography variant="body2" color="text.secondary">
                Loading collections...
              </Typography>
            </Box>
          ) : collections.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 3, 
              backgroundColor: '#f9f9f9', 
              borderRadius: '8px',
              border: '1px dashed #ddd'
            }}>
              <CollectionIcon sx={{ color: '#ccc', fontSize: '3rem', mb: 1 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                No collections available yet
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Create some collections first to organize your projects automatically.
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Collection Selection Grid */}
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 600, 
                color: '#2e7d2e', 
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <CollectionIcon sx={{ fontSize: '1rem' }} />
                Available Collections ({collections.length})
              </Typography>
              
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
                gap: 2,
                mb: 2
              }}>
                {collections.map((collection) => {
                  const isSelected = selectedCollectionIds.includes(collection.id);
                  return (
                    <Card
                      key={collection.id}
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        border: isSelected 
                          ? '2px solid #2e7d2e' 
                          : '1px solid rgba(46, 125, 46, 0.2)',
                        backgroundColor: isSelected 
                          ? 'rgba(46, 125, 46, 0.04)' 
                          : '#fff',
                        boxShadow: isSelected 
                          ? '0 4px 16px rgba(46, 125, 46, 0.15)' 
                          : '0 2px 8px rgba(46, 125, 46, 0.06)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: isSelected 
                            ? '0 8px 24px rgba(46, 125, 46, 0.25)' 
                            : '0 6px 20px rgba(46, 125, 46, 0.12)',
                          borderColor: isSelected ? '#2e7d2e' : 'rgba(46, 125, 46, 0.4)',
                        },
                        borderRadius: '12px',
                        overflow: 'hidden'
                      }}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedCollectionIds(prev => prev.filter(id => id !== collection.id));
                        } else {
                          setSelectedCollectionIds(prev => [...prev, collection.id]);
                        }
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '10px',
                              background: isSelected 
                                ? 'linear-gradient(135deg, #2e7d2e 0%, #4e9d4e 100%)'
                                : 'linear-gradient(135deg, rgba(46, 125, 46, 0.6) 0%, rgba(78, 157, 78, 0.6) 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: isSelected 
                                ? '0 4px 12px rgba(46, 125, 46, 0.3)'
                                : '0 2px 8px rgba(46, 125, 46, 0.15)',
                              flexShrink: 0
                            }}
                          >
                            <CollectionIcon sx={{ 
                              color: 'white', 
                              fontSize: '1.2rem'
                            }} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ 
                              fontWeight: 600,
                              color: isSelected ? '#2e7d2e' : '#333',
                              mb: 0.5,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {collection.title}
                            </Typography>
                            {collection.description && (
                              <Typography variant="body2" sx={{ 
                                color: 'text.secondary',
                                fontSize: '0.85rem',
                                lineHeight: 1.4,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                              }}>
                                {collection.description}
                              </Typography>
                            )}
                            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                              {collection.project_count !== undefined && (
                                <Chip 
                                  label={`${collection.project_count} projects`}
                                  size="small"
                                  sx={{
                                    height: '20px',
                                    fontSize: '0.7rem',
                                    backgroundColor: isSelected ? 'rgba(46, 125, 46, 0.1)' : '#f5f5f5',
                                    color: isSelected ? '#2e7d2e' : '#666',
                                    border: isSelected ? '1px solid rgba(46, 125, 46, 0.3)' : '1px solid #ddd'
                                  }}
                                />
                              )}
                              {isSelected && (
                                <Chip 
                                  label="Selected"
                                  size="small"
                                  sx={{
                                    height: '20px',
                                    fontSize: '0.7rem',
                                    backgroundColor: '#2e7d2e',
                                    color: 'white',
                                    fontWeight: 600
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>

              {/* Selection Summary */}
              {selectedCollectionIds.length > 0 && (
                <Box sx={{ 
                  mt: 2, 
                  p: 2, 
                  backgroundColor: 'rgba(46, 125, 46, 0.04)',
                  borderRadius: '8px',
                  border: '1px solid rgba(46, 125, 46, 0.2)'
                }}>
                  <Typography variant="subtitle2" sx={{ 
                    fontWeight: 600, 
                    color: '#2e7d2e', 
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <CollectionIcon sx={{ fontSize: '1rem' }} />
                    Selected Collections ({selectedCollectionIds.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedCollectionIds.map((collectionId) => {
                      const collection = collections.find(c => c.id === collectionId);
                      return collection ? (
                        <Chip
                          key={collectionId}
                          label={collection.title}
                          onDelete={() => {
                            setSelectedCollectionIds(prev => prev.filter(id => id !== collectionId));
                          }}
                          sx={{
                            backgroundColor: 'rgba(46, 125, 46, 0.1)',
                            color: '#2e7d2e',
                            fontWeight: 600,
                            '& .MuiChip-deleteIcon': {
                              color: '#2e7d2e',
                              '&:hover': {
                                color: '#4e9d4e'
                              }
                            }
                          }}
                        />
                      ) : null;
                    })}
                  </Box>
                  <Button
                    size="small"
                    onClick={() => setSelectedCollectionIds([])}
                    sx={{ 
                      mt: 1,
                      color: '#2e7d2e',
                      textTransform: 'none',
                      fontSize: '0.8rem',
                      '&:hover': {
                        backgroundColor: 'rgba(46, 125, 46, 0.1)'
                      }
                    }}
                  >
                    Clear all selections
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Debug Test Button */}
      <Card sx={{
        mb: 2,
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        border: '1px solid #ff660010',
        overflow: 'hidden',
      }}>
        <CardContent sx={{ p: 2 }}>
          <Button
            variant="contained"
            onClick={handleTestSave}
            sx={{
              backgroundColor: '#ff6600',
              color: 'white',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#e55a00'
              }
            }}
          >
            🧪 Test Save Data (Debug)
          </Button>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#666' }}>
            Click to see what data would be saved (check console and alert)
          </Typography>
        </CardContent>
      </Card>

      {/* Tag dropdown */}
      <TagsDropdown
        tags={tags}
        handleTagSelect={handleTagSelect}
        anchorEl={anchorEl}
        setAnchorEl={handleMainTagMenuClose}
        menuType={menuType}
      />

      {FolderBrowserComponent}
    </ActionSettingsLayout>
  );
};

export default React.memo(ActionsCreateProject);
